import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getToken, setToken, clearToken, loginRequest, getSellerProfile } from '../services/api';
import type { SellerUser } from '../types';

// ── Context Shape ────────────────────────────────────────────────────────────
interface AuthContextType {
  user: SellerUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ── Provider ─────────────────────────────────────────────────────────────────
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SellerUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Validate existing token on app load
  const validateToken = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const profile = await getSellerProfile();
      if (profile && profile.business_name) {
        setUser(profile as SellerUser);
      } else {
        // Token valid but no seller profile
        clearToken();
      }
    } catch (err) {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Login flow
  const login = async (email: string, password: string): Promise<void> => {
    setError(null);
    try {
      const data = await loginRequest(email, password);
      const token = data.access_token;
      if (!token) {
        throw new Error('No access token received');
      }

      setToken(token);

      // Verify this user has a seller account
      try {
        const profile = await getSellerProfile();
        if (profile && profile.business_name) {
          setUser(profile as SellerUser);
        } else {
          clearToken();
          throw new Error('No seller account found. Please register via the Readora app.');
        }
      } catch (profileErr: any) {
        clearToken();
        throw new Error('No seller account found. Please register via the Readora app.');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const message =
        (typeof detail === 'string' ? detail : null) ||
        (typeof err?.message === 'string' && !err.message.includes('[object') ? err.message : null) ||
        'Login failed. Please check your credentials.';
      setError(message);
      throw new Error(message);
    }
  };

  // Logout
  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        isAuthenticated: !!user,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ── Hook ─────────────────────────────────────────────────────────────────────
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
